import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { type CompanyDunning } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useDunningDismissStore, popupKey } from '@/stores/dunning-dismiss-store';
import { dunningPayPath } from '@/lib/dunning-pay-path';
import { useCanOpenPayPath } from '@/hooks/useCanOpenPayPath';

/**
 * Reads the account's dunning projection and derives what the portal surfaces need —
 * the banner and the login popup.
 *
 * **Source of truth is `me.payment.dunning`, deliberately — not `getCompanyInfo`.**
 * The backend re-reads it fresh from Mongo on every `me` call (the rest of `payment`
 * comes from a 15-minute Redis cache). Reading it anywhere else would reintroduce
 * exactly the staleness that fresh read exists to kill: a customer who has just paid
 * must never still see "your screens stop playing on the 24th".
 *
 * This hook decides nothing about the ladder. Which day, which rung, which arm, and
 * now even WHAT TO SHOW — all of that is the backend's. The episode lives in
 * staff-app's Postgres, and what arrives here is four already-made decisions
 * (`showBanner`, `showPopup`, `isLimited`, `shutoffAt`). The frontend renders them.
 */

/**
 * Pages where the banner is suppressed. Matches `usePromotionalBanner`'s blocklist:
 * these are full-bleed editing surfaces where a banner breaks layout.
 *
 * This is the one surface decision that is genuinely the frontend's — it is about our
 * layout, not about the ladder.
 */
const EXCLUDED_PATHS = ['/app/designManagement'];

/** sessionStorage key prefix for the popup's once-per-session dismissal. */
const POPUP_DISMISS_KEY = 'optisigns_dunning_notice_seen';

/**
 * Narrow the untyped `me.payment` JSON blob to the dunning projection.
 *
 * A type guard rather than a cast — `payment` is a `Record<string, unknown>` on the
 * wire, and a malformed value must render nothing rather than crash the shell.
 *
 * It deliberately requires NOTHING beyond "an object that asks for something". The
 * previous version required `state` and `ladder`, which the backend no longer sends;
 * left as it was, every banner would have silently disappeared — the worst failure
 * mode available here, because nothing throws and nobody notices.
 *
 * EXPORTED because `useDunningAdvisory` needs the same answer to decide whether a real
 * episode outranks the advisory. It had its own copy keyed on `state`, which meant it
 * never found an episode and never suppressed anything — the same bug as above, in the
 * sibling hook, surviving because each file looked right on its own.
 */
export function readDunning(payment: Record<string, unknown> | undefined): CompanyDunning | null {
  const raw = payment?.dunning;
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as CompanyDunning;
  const rendersSomething =
    candidate.showBanner === true || candidate.showPopup === true || candidate.isLimited === true;
  return rendersSomething ? candidate : null;
}

function hasSeenPopup(key: string): boolean {
  try {
    return sessionStorage.getItem(`${POPUP_DISMISS_KEY}_${key}`) === '1';
  } catch {
    // Private browsing / storage disabled — fall back to showing it. Losing a
    // dismissal costs one extra popup; throwing costs the shell.
    return false;
  }
}

function markPopupSeen(key: string): void {
  try {
    sessionStorage.setItem(`${POPUP_DISMISS_KEY}_${key}`, '1');
  } catch {
    // Best-effort only — the in-memory store still closes it for this tab.
  }
}

export interface DunningStateResult {
  /** The live projection, or null when there is nothing to show. */
  dunning: CompanyDunning | null;
  /** Where the pay CTA goes. One rule, shared with every other dunning surface. */
  payPath: string;
  /** The viewer can open `payPath`; false hides the CTA (see `canOpenPayPath`). */
  canPay: boolean;
  /** Show the persistent in-portal banner. */
  showBanner: boolean;
  /** Show the login popup — once per browser session, dismissible. */
  showPopup: boolean;
  /**
   * New screens, users and publishing are blocked. The backend's own answer, the same
   * flag open-api's guard enforces; see `isContentCreationBlocked` for the create path.
   */
  isLimited: boolean;
  /** Dismiss the popup for this browser session (sessionStorage only; nothing is sent). */
  acknowledge: () => void;
}

export function useDunningState(): DunningStateResult {
  const location = useLocation();
  const me = useAuthStore((s) => s.me);

  const dismissed = useDunningDismissStore((s) => s.dismissed);
  const dismiss = useDunningDismissStore((s) => s.dismiss);

  const dunning = useMemo(() => readDunning(me?.payment), [me?.payment]);

  const isExcludedPath = EXCLUDED_PATHS.some((path) => location.pathname.startsWith(path));

  const showBanner =
    // Wait for auth to settle so the banner can't flash before state is known.
    !!me && dunning?.showBanner === true && !isExcludedPath;

  /**
   * One dismissal per account per browser session, and NOT recorded server-side —
   * neither "Not now" nor the pay click leaves any durable record (product decision,
   * 2026-10). `popupKey` is the account id only: not per rung, not per episode.
   *
   * It used to be one per ladder rung, recorded server-side, which needed the rung id
   * and the episode's start date in the projection purely to build a key. Session
   * scope gives the same courtesy — dismiss it and it stays gone while you work — and
   * it returns at the next sign-in, which is when a still-unpaid account should be
   * reminded. The in-memory store closes it instantly for every mounted consumer;
   * sessionStorage survives a reload and a `me` refetch, so a focus refetch cannot pop
   * it back up mid-session.
   */
  const seenKey = popupKey(me?.accountId);
  const locallyDismissed = dismissed.has(seenKey);

  const showPopup =
    !!me &&
    dunning?.showPopup === true &&
    !locallyDismissed &&
    !hasSeenPopup(seenKey) &&
    !isExcludedPath;

  const acknowledge = useCallback(() => {
    markPopupSeen(seenKey);
    dismiss(seenKey);
  }, [seenKey, dismiss]);

  const payPath = dunningPayPath();
  const canPay = useCanOpenPayPath(payPath);

  return {
    dunning,
    payPath,
    canPay,
    showBanner,
    showPopup,
    isLimited: dunning?.isLimited === true,
    acknowledge,
  };
}
